import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import { Stack, Typography, Box, TextField, CircularProgress } from "@mui/material";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";

import V2PageHeader from "../../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../../common/SiteScopeGate";
import StickySaveBar from "../../../../../common/StickySaveBar";
import { GhostButton } from "../../../../../common/Button";
import { GreenSwitch } from "../../../../../common/Greens";
import colors from "../../../../../../utils/colors";
import { AsecModeSegmented } from "../../api_security/components/asecPrimitives";
import { useNotification } from "../../../../../../contexts/NotificationContext";
import useCspConfig from "../../../../../../hooks/user/useCspConfig";
import useGlobalSite from "../../../../../../hooks/user/useGlobalSite";

const INDIGO = colors.primary[300];
const BORDER = colors.neutral[200];
const FG1 = colors.neutral[1000];
const FG2 = colors.neutral[700];
const FG3 = colors.neutral[500];

const MODES = [
  {
    key: "off",
    label: "Off",
    desc: "No header injected. Zero impact on traffic.",
    Icon: BlockOutlinedIcon,
  },
  {
    key: "discovery",
    label: "Discovery",
    desc: "Layerd ships a maximally strict report-only policy so every subresource fires a violation. Use it to learn your site's real script inventory before authoring a policy.",
    Icon: ExploreOutlinedIcon,
  },
  {
    key: "report_only",
    label: "Report only",
    desc: "Browsers report violations but do not block. Safe rollout mode for the policy you've authored.",
    Icon: BugReportOutlinedIcon,
  },
  {
    key: "enforce",
    label: "Enforce",
    desc: "Browsers block matching violations AND report. Live protection.",
    Icon: ShieldOutlinedIcon,
  },
];

function emptyForm() {
  return {
    path_pattern: "",
    mode: "enforce",
    policy_text: "",
    enabled: true,
  };
}

function rowToForm(row) {
  if (!row) return emptyForm();
  return {
    path_pattern: row.path_pattern || "",
    mode: row.mode || "enforce",
    policy_text: row.policy_text || "",
    enabled: row.enabled !== false,
  };
}

function ModePickerRow({ current, onPick }) {
  const options = MODES.map(({ key, label, Icon }) => ({
    value: key,
    label,
    icon: <Icon sx={{ fontSize: 14 }} />,
    tone: key === "off" ? "off" : key === "enforce" ? "enforce" : key === "report_only" ? "warn" : "info",
  }));

  return (
    <Stack spacing={1}>
      <AsecModeSegmented value={current} onChange={onPick} options={options} />
      <Typography sx={{ fontSize: 12, color: FG2, lineHeight: 1.45 }}>
        {MODES.find((m) => m.key === current)?.desc}
      </Typography>
    </Stack>
  );
}

ModePickerRow.propTypes = {
  current: PropTypes.string.isRequired,
  onPick: PropTypes.func.isRequired,
};

function SectionCard({ title, desc, children }) {
  return (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${BORDER}`,
        borderRadius: "14px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          padding: "18px 20px 14px",
          display: "flex",
          gap: 1.5,
          alignItems: "flex-start",
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "9px",
            background: "rgba(99, 113, 247, 0.08)",
            color: INDIGO,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            "& svg": { width: 18, height: 18 },
          }}
        >
          <PolicyOutlinedIcon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 16, color: FG1, mb: 0.25 }}>
            {title}
          </Typography>
          {desc && (
            <Typography sx={{ fontSize: 12.5, color: FG2, lineHeight: 1.5, maxWidth: 720 }}>
              {desc}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ padding: "20px 20px 22px" }}>{children}</Box>
    </Box>
  );
}

SectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  desc: PropTypes.string,
  children: PropTypes.node,
};

export default function RouteOverrideFormPage({ initialRow, onCancel, onSuccess }) {
  const { effectiveSiteUid } = useGlobalSite();
  const { showSuccess, showError } = useNotification();
  const { upsertRouteOverride, deleteRouteOverride, listRouteOverrides } = useCspConfig();

  const site_uid = effectiveSiteUid || null;
  const isNew = !(initialRow && initialRow.id);
  const baseline = useMemo(() => rowToForm(initialRow), [initialRow]);

  const [form, setForm] = useState(baseline);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(baseline);
  }, [baseline]);

  const patternError = useMemo(() => {
    if (!form.path_pattern) return null;
    if (!form.path_pattern.startsWith("/")) return "Must start with /";
    if (form.path_pattern.includes("?") || form.path_pattern.includes("#")) return "Cannot contain ? or #";
    if (/\s/.test(form.path_pattern)) return "Cannot contain whitespace";
    if (form.path_pattern.length > 512) return "Too long (max 512)";
    return null;
  }, [form.path_pattern]);

  const dirty = useMemo(
    () =>
      form.path_pattern !== baseline.path_pattern
      || form.mode !== baseline.mode
      || form.policy_text !== baseline.policy_text
      || form.enabled !== baseline.enabled,
    [form, baseline]
  );

  const modifiedFields = useMemo(() => {
    const out = [];
    if (form.path_pattern !== baseline.path_pattern) out.push("Path prefix");
    if (form.mode !== baseline.mode) out.push("Mode");
    if (form.policy_text !== baseline.policy_text) out.push("Policy text");
    if (form.enabled !== baseline.enabled) out.push("Enabled");
    return out;
  }, [form, baseline]);

  const canSave = !!form.path_pattern && !patternError && !!form.mode && !saving;

  const handleDiscard = useCallback(() => {
    setForm(baseline);
  }, [baseline]);

  const handleSave = useCallback(async () => {
    if (!site_uid || !canSave) return;
    setSaving(true);
    const path = form.path_pattern.trim();
    const previousPath = (baseline.path_pattern || "").trim();
    try {
      // Path is the upsert key — renaming onto an existing override would overwrite it.
      if (!isNew && path !== previousPath && initialRow?.id) {
        const rowsPayload = await listRouteOverrides(site_uid);
        const rows = Array.isArray(rowsPayload)
          ? rowsPayload
          : (rowsPayload?.overrides || []);
        const conflict = rows.find(
          (r) => r?.path_pattern === path && r?.id !== initialRow.id,
        );
        if (conflict) {
          showError(
            `An override for ${path} already exists. Choose a different path or edit that override.`,
          );
          return;
        }
      }

      const saved = await upsertRouteOverride(site_uid, {
        path_pattern: path,
        mode: form.mode,
        policy_text: form.policy_text || "",
        enabled: form.enabled,
      });
      // Upsert keys on path_pattern — a rename creates a new row; drop the old one.
      if (
        !isNew
        && initialRow?.id
        && path !== previousPath
        && saved?.id
        && saved.id !== initialRow.id
      ) {
        try {
          await deleteRouteOverride(site_uid, initialRow.id);
        } catch (delErr) {
          showError(
            delErr?.response?.data?.message
              || delErr?.message
              || "Path saved, but the previous override could not be removed. Delete it from the list if it still appears.",
          );
          onSuccess?.();
          return;
        }
      }
      showSuccess(`Route override for ${path} saved.`);
      onSuccess?.();
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }, [
    site_uid,
    canSave,
    form,
    baseline.path_pattern,
    isNew,
    initialRow,
    upsertRouteOverride,
    deleteRouteOverride,
    listRouteOverrides,
    showSuccess,
    showError,
    onSuccess,
  ]);

  const pageTitle = isNew ? "Add route override" : "Edit route override";

  return (
    <SiteScopeGate mode="single">
      {() => (
        <>
          <Helmet title={pageTitle} />
          <Stack spacing={3} sx={{ p: 4, pb: dirty ? 12 : 4 }}>
            <V2PageHeader
              title={pageTitle}
              sx={{ pb: 1.5 }}
              descriptionSx={{ maxWidth: 760, mt: 1 }}
              description="Per-path CSP policy override. On each request, the longest matching path prefix wins. Report-URI, SRI, and Trusted Types stay site-wide."
              actions={(
                <GhostButton size="small" onClick={onCancel}>
                  Cancel
                </GhostButton>
              )}
            />

            <SectionCard
              title="Override details"
              desc="Set the path prefix this override applies to. Use mode Off with empty policy text to exempt a path from CSP entirely."
            >
              <Stack spacing={2.75}>
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Path prefix
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={form.path_pattern}
                    onChange={(e) => setForm((f) => ({ ...f, path_pattern: e.target.value }))}
                    placeholder="/checkout"
                    error={!!patternError}
                    helperText={patternError || "Longest match wins. /api matches /api/x but not /apix. /api/ scopes to descendants only."}
                    inputProps={{
                      maxLength: 512,
                      style: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
                    }}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Mode
                  </Typography>
                  <ModePickerRow
                    current={form.mode}
                    onPick={(mode) => setForm((f) => ({ ...f, mode }))}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Policy text
                  </Typography>
                  <TextField
                    multiline
                    minRows={6}
                    maxRows={18}
                    fullWidth
                    value={form.policy_text}
                    onChange={(e) => setForm((f) => ({ ...f, policy_text: e.target.value }))}
                    placeholder="default-src 'self'; script-src 'self'"
                    inputProps={{
                      maxLength: 64000,
                      style: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 },
                    }}
                  />
                  <Typography sx={{ fontSize: 11.5, color: FG3, mt: 0.75 }}>
                    Report-URI, SRI, and Trusted Types are inherited from the site-wide config. Leave empty with mode=Off to exempt this path from CSP entirely.
                  </Typography>
                </Box>

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 0.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: FG1 }}>Enabled</Typography>
                    <Typography sx={{ fontSize: 12, color: FG2 }}>
                      Toggle off to keep the row but stop applying it — useful for staged rollback.
                    </Typography>
                  </Box>
                  <GreenSwitch
                    checked={form.enabled}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  />
                </Stack>
              </Stack>
            </SectionCard>

            {saving && (
              <Stack alignItems="center">
                <CircularProgress size={24} sx={{ color: INDIGO }} />
              </Stack>
            )}
          </Stack>

          <StickySaveBar
            open={dirty}
            modifiedCount={modifiedFields.length}
            modifiedFields={modifiedFields}
            onDiscard={handleDiscard}
            onApply={canSave ? handleSave : undefined}
            isApplying={saving}
          />
        </>
      )}
    </SiteScopeGate>
  );
}

RouteOverrideFormPage.propTypes = {
  initialRow: PropTypes.object,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
};
